import React, {useState} from 'react';
import { bool, func, shape, string } from 'prop-types';
import { compose } from 'redux';
import { Form as FinalForm } from 'react-final-form';
import { intlShape, injectIntl, FormattedMessage } from '../../util/reactIntl';
import classNames from 'classnames';

import { propTypes } from '../../util/types';
import {
  autocompletePlaceSelected,
  autocompleteSearchRequired,
  composeValidators,
  required
} from '../../util/validators';
import {
  Form,
  PrimaryButton,
  FieldTextInput,
  FieldSelect,
  LocationAutocompleteInputField,
  FieldNumericStepper,
} from '../../components';

import css from './EditListingPresentationForm.module.css';

const EditListingPresentationFormComponent = (props) => {
  const [showAutocompleteField, setShowAutocompleteField] = useState(false);

  return (
    <FinalForm
      {...props}
      render={(formRenderProps) => {
        const {
          className,
          disabled,
          ready,
          handleSubmit,
          intl,
          invalid,
          pristine,
          saveActionMsg,
          updated,
          updateInProgress,
          fetchErrors,
          values,
        } = formRenderProps;
        const locationLabel = intl.formatMessage({ id: 'EditListingPresentationForm.locationLabel' });
        const locationPlaceholder = intl.formatMessage({
          id: 'EditListingPresentationForm.locationPlaceholder',
        });
        const locationRequiredMessage = intl.formatMessage({
          id: 'EditListingPresentationForm.locationRequired',
        });
        const locationErrorMessage = intl.formatMessage({
          id: 'EditListingPresentationForm.locationError',
        });
        const addressDetailsTitle = intl.formatMessage({
          id: 'EditListingPresentationForm.addressDetailsTitle',
        });
        const addressLine1Label = intl.formatMessage({
          id: 'EditListingPresentationForm.addressLine1Label',
        });
        const addressLine1Placeholder = intl.formatMessage({
          id: 'EditListingPresentationForm.addressLine1Placeholder',
        });
        const cityLabel = intl.formatMessage({ id: 'EditListingPresentationForm.cityLabel' });
        const cityPlaceholder = intl.formatMessage({
          id: 'EditListingPresentationForm.cityPlaceholder',
        });
        const postalCodeLabel = intl.formatMessage({
          id: 'EditListingPresentationForm.postalCodeLabel',
        });
        const postalCodePlaceholder = intl.formatMessage({
          id: 'EditListingPresentationForm.postalCodePlaceholder',
        });
        const stateLabel = intl.formatMessage({ id: 'EditListingPresentationForm.stateLabel' });
        const statePlaceholder = intl.formatMessage({
          id: 'EditListingPresentationForm.statePlaceholder',
        });
        const countryLabel = intl.formatMessage({ id: 'EditListingPresentationForm.countryLabel' });
        const countryPlaceholder = intl.formatMessage({
          id: 'EditListingPresentationForm.countryPlaceholder',
        });
        const addressDetailsResetLabel = intl.formatMessage({
          id: 'EditListingPresentationForm.addressDetailsReset',
        });
        const maxCapacityLabel = intl.formatMessage({
          id: 'EditListingPresentationForm.maxCapacityLabel',
        });

        const { updateListingError, showListingsError } = fetchErrors || {};
        const errorMessageUpdateListing = updateListingError ? (
          <p className={css.error}>
            <FormattedMessage id="EditListingPresentationForm.updateFailed" />
          </p>
        ) : null;

        const errorMessageShowListing = showListingsError ? (
          <p className={css.error}>
            <FormattedMessage id="EditListingPresentationForm.showListingFailed" />
          </p>
        ) : null;

        const locationValue = values && values.location ? values.location : {};
        const hasExistingAddressDetails =
          locationValue.selectedPlace && locationValue.selectedPlace.addressDetails;

        const classes = classNames(css.root, className);
        const submitReady = (updated && pristine) || ready;
        const submitInProgress = updateInProgress;
        const submitDisabled = invalid || disabled || submitInProgress;

        return (
          <Form className={classes} onSubmit={handleSubmit}>
            {errorMessageUpdateListing}
            {errorMessageShowListing}

            {(showAutocompleteField || !hasExistingAddressDetails) ? (
              <LocationAutocompleteInputField
                className={css.locationAddress}
                inputClassName={css.locationAutocompleteInput}
                iconClassName={css.locationAutocompleteInputIcon}
                predictionsClassName={css.predictionsRoot}
                validClassName={css.validLocation}
                name="location"
                label={locationLabel}
                placeholder={locationPlaceholder}
                useDefaultPredictions={false}
                format={null}
                valueFromForm={values.location}
                validate={composeValidators(
                  autocompleteSearchRequired(locationRequiredMessage),
                  autocompletePlaceSelected(locationErrorMessage),
                )}
                handleSelectPlaceDone={() => {
                  setShowAutocompleteField(false);
                }}
              />
            ) :  (
              <div className={css.addressDetailsSection}>
                <div className={css.addressDetailsHeader}>
                  <h3 className={css.addressDetailsTitle}>{addressDetailsTitle}</h3>
                  <button
                    className={css.addressDetailsResetButton}
                    type="button"
                    onClick={() => {
                      if (values && values.location && values.location.onChange) {
                        const current = values.location.value || {};
                        values.location.onChange({
                          ...current,
                          showAddressDetailsForm: false,
                        });
                      }
                      setShowAutocompleteField(true);
                    }}
                  >
                    {addressDetailsResetLabel}
                  </button>
                </div>

                <div className={css.addressDetailsFields}>
                  <FieldTextInput
                    className={css.addressDetailsField}
                    name="location.selectedPlace.addressDetails.line1"
                    id="locationAddressLine1"
                    label={addressLine1Label}
                    placeholder={addressLine1Placeholder}
                    validate={required(locationRequiredMessage)}
                  />

                  <FieldTextInput
                    className={css.addressDetailsField}
                    name="location.selectedPlace.addressDetails.city"
                    id="locationCity"
                    label={cityLabel}
                    placeholder={cityPlaceholder}
                    validate={required(locationRequiredMessage)}
                  />

                  <FieldTextInput
                    className={css.addressDetailsField}
                    name="location.selectedPlace.addressDetails.postalCode"
                    id="locationPostalCode"
                    label={postalCodeLabel}
                    placeholder={postalCodePlaceholder}
                    validate={required(locationRequiredMessage)}
                  />

                  <FieldTextInput
                    className={css.addressDetailsField}
                    name="location.selectedPlace.addressDetails.state"
                    id="locationState"
                    label={stateLabel}
                    placeholder={statePlaceholder}
                    validate={required(locationRequiredMessage)}
                  />

                  <FieldSelect
                    className={css.addressDetailsField}
                    name="location.selectedPlace.addressDetails.country"
                    id="locationCountry"
                    label={countryLabel}
                    validate={required(locationRequiredMessage)}
                  >
                    <option value="">{countryPlaceholder}</option>
                    <option value="FR">France</option>
                    <option value="ES">Espagne</option>
                    <option value="BE">Belgique</option>
                    <option value="IT">Italie</option>
                    <option value="US">États-Unis</option>
                  </FieldSelect>
                </div>
              </div>
            )}

            <div className={css.capacityContainer}>
              <FieldNumericStepper
                className={css.capacityItem}
                min={1}
                id="maxCapacityToGuest"
                name="maxCapacityToGuest"
                label={maxCapacityLabel}
              />
            </div>

            <div className={css.buttonContainer}>
              <PrimaryButton
                className={css.submitButton}
                type="submit"
                inProgress={submitInProgress}
                disabled={submitDisabled}
                ready={submitReady}
              >
                {saveActionMsg}
              </PrimaryButton>
            </div>
          </Form>
        );
      }}
    />
  );
}

EditListingPresentationFormComponent.defaultProps = {
  className: null,
  selectedPlace: null,
  fetchErrors: null,
};

EditListingPresentationFormComponent.propTypes = {
  intl: intlShape.isRequired,
  className: string,
  onSubmit: func.isRequired,
  saveActionMsg: string.isRequired,
  selectedPlace: propTypes.place,
  disabled: bool.isRequired,
  ready: bool.isRequired,
  updated: bool.isRequired,
  updateInProgress: bool.isRequired,
  fetchErrors: shape({
    showListingsError: propTypes.error,
    updateListingError: propTypes.error,
  }),
};

export default compose(injectIntl)(EditListingPresentationFormComponent);
